import React, { useState } from 'react';
import CountdownTimer from '../Voting/CountdownTimer';

interface CarouselItem {
  fileId: string;
  url: string;
  caption?: string;
}

interface VotingSession {
  sessionId: string;
  status: string;
  startDate: string;
  endDate: string;
  question?: string;
  type?: 'open' | 'anonymous';
}

interface ProjectHeroCarouselProps {
  items: CarouselItem[];
  nearestVotingSession?: VotingSession | null;
  activeVotingSession?: VotingSession | null;
  onVoteClick?: () => void;
  isOwner?: boolean;
}

const ProjectHeroCarousel: React.FC<ProjectHeroCarouselProps> = ({ 
  items, 
  nearestVotingSession,
  activeVotingSession,
  onVoteClick,
  isOwner = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Find nearest scheduled session
  const scheduledSession = nearestVotingSession && nearestVotingSession.status === 'scheduled'
    ? nearestVotingSession
    : null;

  // Active session takes priority
  const hasActiveSession = activeVotingSession && activeVotingSession.status === 'active';

  if (items.length === 0 && !scheduledSession && !hasActiveSession) {
    return null;
  }

  // If no images but there's an active session, show voting banner
  if (items.length === 0 && hasActiveSession && activeVotingSession) {
    return (
      <div className="relative w-full bg-gradient-to-r from-[var(--color-success-600)] to-[var(--color-success-700)] rounded-lg overflow-hidden">
        <div className="p-8 text-white">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-2xl font-bold">Voting Session Active</h3>
                </div>
                {activeVotingSession.question && (
                  <p className="text-green-100 mb-4 text-lg">{activeVotingSession.question}</p>
                )}
                <div className="text-sm text-green-100 mb-4">
                  <p>Ends: {new Date(activeVotingSession.endDate).toLocaleString()}</p>
                  {activeVotingSession.type && (
                    <p className="capitalize">Type: {activeVotingSession.type}</p>
                  )}
                </div>
                {onVoteClick && (
                  <button
                    onClick={onVoteClick}
                    className="mt-4 bg-white text-green-600 font-semibold px-6 py-3 rounded-lg hover:bg-green-50 transition-colors shadow-lg animate-pulse"
                  >
                    Vote Now
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 bg-white bg-opacity-20 rounded-lg px-4 py-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-semibold">Vote Now</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If no images but there's a scheduled session, show countdown banner
  if (items.length === 0 && scheduledSession) {
    return (
      <div className="relative w-full bg-gradient-to-r from-[var(--color-brand-600)] to-[var(--color-brand-700)] rounded-lg overflow-hidden">
        <div className="p-8 text-white">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-2">Upcoming Voting Session</h3>
                {scheduledSession.question && (
                  <p className="text-blue-100 mb-4">{scheduledSession.question}</p>
                )}
                <CountdownTimer
                  targetDate={scheduledSession.startDate}
                  className="text-lg"
                  variant="hero"
                />
              </div>
              <div className="flex items-center gap-2 bg-white bg-opacity-20 rounded-lg px-4 py-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-semibold">Voting Starts Soon</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <div className="relative w-full h-64 md:h-96 bg-[var(--color-neutral-200)] rounded-lg overflow-hidden">
      {/* Active Voting Session Overlay */}
      {hasActiveSession && activeVotingSession && (
        <div className="absolute top-4 left-4 right-4 z-20 bg-gradient-to-r from-[var(--color-success-600)] to-[var(--color-success-700)] text-white rounded-lg shadow-lg p-4 animate-pulse">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-semibold text-sm">Voting Session Active</span>
              </div>
              {activeVotingSession.question && (
                <p className="text-xs text-green-100 mb-2 line-clamp-2">{activeVotingSession.question}</p>
              )}
              <div className="text-xs text-green-100">
                <p>Ends: {new Date(activeVotingSession.endDate).toLocaleString()}</p>
              </div>
            </div>
            {onVoteClick && (
              <button
                onClick={onVoteClick}
                className="bg-white text-green-600 font-semibold px-4 py-2 rounded-lg hover:bg-green-50 transition-colors text-sm whitespace-nowrap"
              >
                Vote Now
              </button>
            )}
          </div>
        </div>
      )}

      {/* Voting Session Countdown Overlay */}
      {!hasActiveSession && scheduledSession && (
        <div className="absolute top-4 right-4 z-20 bg-gradient-to-r from-[var(--color-brand-600)] to-[var(--color-brand-700)] text-white rounded-lg shadow-lg p-4 max-w-sm animate-pulse">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-semibold text-sm">Voting Session Starting</span>
          </div>
          {scheduledSession.question && (
            <p className="text-xs text-blue-100 mb-2 line-clamp-2">{scheduledSession.question}</p>
          )}
          <CountdownTimer
            targetDate={scheduledSession.startDate}
            className="text-sm"
            variant="hero"
          />
        </div>
      )}

      {items.map((item, index) => (
        <div
          key={item.fileId}
          className={`absolute inset-0 transition-opacity duration-500 ${
            index === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={item.url}
            alt={item.caption || `Slide ${index + 1}`}
            className="w-full h-full object-cover"
          />
          {item.caption && (
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-4">
              <p className="text-sm">{item.caption}</p>
            </div>
          )}
        </div>
      ))}

      {items.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-75 hover:bg-opacity-100 rounded-full p-2 transition-all"
            aria-label="Previous slide"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-75 hover:bg-opacity-100 rounded-full p-2 transition-all"
            aria-label="Next slide"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {items.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectHeroCarousel;

